import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  AutoFixHigh as AIIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MedicalServices as MedIcon,
  Opacity as FluidIcon,
  MonitorHeart as MonitorIcon,
  LocalHospital as ProcedureIcon,
  Restaurant as NutritionIcon,
  Category as OtherIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { endOfDay } from 'date-fns';
import { treatmentSheetApi, SuggestedOrder } from '../../utils/api';
import type { TreatmentSheet } from '../../types/treatmentSheet';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  MEDICATION: { label: 'Medication', color: '#1976d2', icon: <MedIcon fontSize="small" /> },
  FLUID:      { label: 'Fluid',      color: '#00897b', icon: <FluidIcon fontSize="small" /> },
  MONITORING: { label: 'Monitoring', color: '#7b1fa2', icon: <MonitorIcon fontSize="small" /> },
  PROCEDURE:  { label: 'Procedure',  color: '#e65100', icon: <ProcedureIcon fontSize="small" /> },
  NUTRITION:  { label: 'Nutrition',  color: '#558b2f', icon: <NutritionIcon fontSize="small" /> },
  OTHER:      { label: 'Other',      color: '#546e7a', icon: <OtherIcon fontSize="small" /> },
};

const FREQUENCIES = [
  'Q1H','Q2H','Q4H','Q6H','Q8H','Q12H','SID','BID','TID','QID','PRN','ONCE',
];

const CATEGORIES = ['MEDICATION','FLUID','MONITORING','PROCEDURE','NUTRITION','OTHER'];

// ─── OrderCard ────────────────────────────────────────────────────────────────

interface OrderCardProps {
  order: SuggestedOrder;
  index: number;
  onChange: (index: number, updated: SuggestedOrder) => void;
  onRemove: (index: number) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, index, onChange, onRemove }) => {
  const [editing, setEditing] = useState(false);
  const [showWarnings, setShowWarnings] = useState(order.warnings.length > 0);
  const cfg = CATEGORY_CONFIG[order.category] ?? CATEGORY_CONFIG.OTHER;
  const hasWarnings = order.warnings.length > 0;
  const hasHighWarning = order.warnings.some((w) => w.startsWith('⚠'));

  const update = (field: keyof SuggestedOrder, value: any) =>
    onChange(index, { ...order, [field]: value });

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: hasHighWarning ? 'warning.main' : 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        // Keep full height inside the scrolling flex-column list — without this,
        // flexbox shrinks each card to fit maxHeight and clips/overlaps the content.
        flexShrink: 0,
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          px: 1.5, py: 1,
          borderLeft: `4px solid ${cfg.color}`,
          display: 'flex', alignItems: 'center', gap: 1,
        }}
      >
        <Box sx={{ color: cfg.color, display: 'flex', alignItems: 'center' }}>{cfg.icon}</Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <TextField
              value={order.name}
              onChange={(e) => update('name', e.target.value)}
              size="small"
              fullWidth
              autoFocus
              variant="standard"
              inputProps={{ style: { fontWeight: 700, fontSize: 14 } }}
            />
          ) : (
            <Tooltip title={order.name} placement="top-start">
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {order.name}
              </Typography>
            </Tooltip>
          )}
          {order.suggestedCanonicalName && order.suggestedCanonicalName !== order.name && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              → {order.suggestedCanonicalName}
            </Typography>
          )}
        </Box>

        {/* Right-side controls — fixed cluster so a long name can't clip or overlap them */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {/* Confidence badge */}
          <Chip
            label={`${Math.round(order.confidence * 100)}%`}
            size="small"
            color={order.confidence >= 0.8 ? 'success' : order.confidence >= 0.6 ? 'warning' : 'default'}
            variant="outlined"
            sx={{ fontSize: 10, height: 18, mr: 0.5 }}
          />

          {hasWarnings && (
            <Tooltip title={`${order.warnings.length} warning${order.warnings.length > 1 ? 's' : ''}`}>
              <IconButton size="small" onClick={() => setShowWarnings((v) => !v)}
                sx={{ color: hasHighWarning ? 'warning.main' : 'text.secondary' }}>
                <WarningIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => setEditing((v) => !v)}>
              {editing ? <CheckIcon fontSize="small" color="success" /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={() => onRemove(index)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Details row */}
      <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'grey.50', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {editing ? (
          <>
            <TextField label="Dose" value={order.dose ?? ''} onChange={(e) => update('dose', e.target.value)}
              size="small" sx={{ width: 100 }} />
            <TextField label="Route" value={order.route ?? ''} onChange={(e) => update('route', e.target.value)}
              size="small" sx={{ width: 80 }} />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Frequency</InputLabel>
              <Select value={order.frequency} label="Frequency" onChange={(e) => update('frequency', e.target.value)}>
                {FREQUENCIES.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select value={order.category} label="Category" onChange={(e) => update('category', e.target.value)}>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            {order.dose && (
              <Chip label={order.dose} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            )}
            {order.route && (
              <Chip label={order.route} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            )}
            <Chip label={order.frequency} size="small" variant="outlined" sx={{ fontSize: 11 }} />
            <Chip label={cfg.label} size="small" sx={{ fontSize: 11, bgcolor: cfg.color + '20', color: cfg.color }} />
          </>
        )}
      </Box>

      {/* Warnings */}
      <Collapse in={showWarnings && hasWarnings}>
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'warning.50', borderTop: '1px solid', borderColor: 'warning.light' }}>
          {order.warnings.map((w, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: i < order.warnings.length - 1 ? 0.5 : 0 }}>
              <WarningIcon sx={{ fontSize: 13, color: w.startsWith('⚠') ? 'warning.dark' : 'text.secondary', mt: 0.2, flexShrink: 0 }} />
              <Typography variant="caption" color={w.startsWith('⚠') ? 'warning.dark' : 'text.secondary'}>
                {w}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>

      {/* Special instructions (editing) */}
      {editing && (
        <Box sx={{ px: 1.5, pb: 1, bgcolor: 'grey.50' }}>
          <TextField
            label="Special Instructions"
            value={order.specialInstructions ?? ''}
            onChange={(e) => update('specialInstructions', e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── AIOrderGenerator ─────────────────────────────────────────────────────────

interface AIOrderGeneratorProps {
  open: boolean;
  sheet: TreatmentSheet;
  onClose: () => void;
}

const AIOrderGenerator: React.FC<AIOrderGeneratorProps> = ({ open, sheet, onClose }) => {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'input' | 'review'>('input');
  const [transcript, setTranscript] = useState('');
  const [orders, setOrders] = useState<SuggestedOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);
  // Snapshot of textarea content at the moment recording started
  const transcriptBeforeRecordingRef = useRef<string>('');
  // Latest voice transcript ref so the "on stop" effect doesn't need it as a dep
  const voiceTranscriptRef = useRef<string>('');

  const horse = sheet.horse;
  const weightKg = horse?.weight;

  const { state: voiceState, start: startVoice, stop: stopVoice, isSupported: voiceSupported } =
    useVoiceRecording({ continuous: true, interimResults: true, maxSilenceDuration: 5000 });

  const recording = voiceState.isRecording;

  // Keep ref in sync with latest voice transcript
  useEffect(() => {
    voiceTranscriptRef.current = voiceState.transcript;
  }, [voiceState.transcript]);

  // Sync live voice transcript into the textarea in real-time
  useEffect(() => {
    if (recording && voiceState.transcript) {
      const base = transcriptBeforeRecordingRef.current;
      setTranscript(base ? `${base}\n${voiceState.transcript}` : voiceState.transcript);
    }
  }, [voiceState.transcript, recording]);

  // When recording stops, commit the final transcript
  useEffect(() => {
    if (!recording) {
      const finalVoice = voiceTranscriptRef.current;
      if (finalVoice) {
        const base = transcriptBeforeRecordingRef.current;
        setTranscript(base ? `${base}\n${finalVoice}`.trim() : finalVoice.trim());
        transcriptBeforeRecordingRef.current = '';
      }
    }
  }, [recording]);

  // Surface any voice errors
  useEffect(() => {
    if (voiceState.error) setError(voiceState.error);
  }, [voiceState.error]);

  const handleClose = () => {
    if (recording) stopVoice();
    setMode('input');
    setTranscript('');
    setOrders([]);
    setError(null);
    setCreatedCount(0);
    onClose();
  };

  // ── Voice recording ────────────────────────────────────────────────────────
  const startRecording = () => {
    transcriptBeforeRecordingRef.current = transcript;
    setError(null);
    startVoice();
  };

  const stopRecording = () => {
    stopVoice();
  };

  // ── AI generation ──────────────────────────────────────────────────────────
  const generateMutation = useMutation(
    () =>
      treatmentSheetApi.generateOrders({
        transcript,
        sheetId: sheet.id,
        horseName: horse?.name,
        horseBreed: horse?.breed,
        horseWeightKg: weightKg,
      }),
    {
      onSuccess: (data) => {
        if (data.success && data.orders.length > 0) {
          setOrders(data.orders);
          setMode('review');
          setError(null);
        } else {
          setError(
            data.message || 'No orders could be parsed. Try adding more detail — e.g. "Banamine 500mg IV q12h, LRS 10L/hr IV".',
          );
        }
      },
      onError: () => setError('Generation failed. Please try again.'),
    },
  );

  // ── Batch create ───────────────────────────────────────────────────────────
  const handleCreateAll = async () => {
    setCreating(true);
    setCreatedCount(0);
    let count = 0;
    for (const o of orders) {
      try {
        // Day-scoped like manual orders: today only; "Duplicate to today"
        // carries them forward. A dictated course length ("x 5 days") is kept
        // as text so the intent survives the durationDays removal.
        const now = new Date();
        const course =
          o.durationDays && o.durationDays > 1 ? `Planned course: ${o.durationDays} days` : undefined;
        await treatmentSheetApi.createOrder(sheet.id, {
          name: o.suggestedCanonicalName ?? o.name,
          category: o.category as any,
          dose: o.dose,
          route: o.route,
          frequency: o.frequency as any,
          startAt: now.toISOString(),
          endAt: endOfDay(now).toISOString(),
          specialInstructions:
            [o.specialInstructions, course].filter(Boolean).join(' — ') || undefined,
        });
        count++;
        setCreatedCount(count);
      } catch {
        // Continue creating remaining orders even if one fails
      }
    }
    queryClient.invalidateQueries(['admin-grid', sheet.id]);
    setCreating(false);
    handleClose();
  };

  const examplePlaceholder = `Example:
Banamine 500mg IV q12h x 3 days
LRS 10L/hr IV continuous
Omeprazole 1g PO SID
Hand walk 20 min TID
Vital signs q4h`;

  const warningCount = orders.reduce((n, o) => n + (o.warnings.some((w) => w.startsWith('⚠')) ? 1 : 0), 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AIIcon color="primary" />
        <Box>
          <Typography variant="h6" component="span">AI Treatment Plan Generator</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {horse?.name} · {horse?.breed}{weightKg ? ` · ${weightKg} kg` : ''}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {mode === 'input' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Dictate or type the treatment plan. Include medications with doses and routes, procedures, monitoring,
              walks, and fluids. The AI will parse them into individual orders and flag any dose concerns.
            </Typography>

            <Box sx={{ position: 'relative' }}>
              <TextField
                multiline
                rows={8}
                fullWidth
                placeholder={examplePlaceholder}
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                variant="outlined"
                label="Treatment Plan"
              />
              {voiceSupported && (
                <Tooltip title={recording ? 'Stop dictation' : 'Dictate treatment plan'}>
                  <IconButton
                    onClick={recording ? stopRecording : startRecording}
                    sx={{
                      position: 'absolute', bottom: 8, right: 8,
                      bgcolor: recording ? 'error.main' : 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: recording ? 'error.dark' : 'primary.dark' },
                      ...(recording && {
                        animation: 'pulse 1.2s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%': { boxShadow: '0 0 0 0 rgba(211,47,47,0.5)' },
                          '70%': { boxShadow: '0 0 0 8px rgba(211,47,47,0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(211,47,47,0)' },
                        },
                      }),
                    }}
                  >
                    {recording ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {recording && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main',
                  animation: 'blink 1s step-start infinite',
                  '@keyframes blink': { '50%': { opacity: 0 } } }} />
                <Typography variant="caption" color="error.main" fontWeight={600}>
                  Listening… speak your treatment plan, then tap the mic to stop
                </Typography>
              </Box>
            )}

            {!weightKg && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Horse weight not on file — dose range warnings will be limited. Add weight in the horse profile for full safety checks.
              </Alert>
            )}
          </Box>
        )}

        {mode === 'review' && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2">
                {orders.length} order{orders.length !== 1 ? 's' : ''} parsed
                {warningCount > 0 && (
                  <Chip
                    label={`${warningCount} dose warning${warningCount > 1 ? 's' : ''}`}
                    size="small"
                    color="warning"
                    sx={{ ml: 1, fontSize: 11 }}
                    icon={<WarningIcon />}
                  />
                )}
              </Typography>
              <Button size="small" onClick={() => setMode('input')}>
                ← Edit Transcript
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 420, overflowY: 'auto', pr: 0.5, py: 0.5 }}>
              {orders.map((order, i) => (
                <OrderCard
                  key={i}
                  index={i}
                  order={order}
                  onChange={(idx, updated) =>
                    setOrders((prev) => prev.map((o, j) => (j === idx ? updated : o)))
                  }
                  onRemove={(idx) =>
                    setOrders((prev) => prev.filter((_, j) => j !== idx))
                  }
                />
              ))}
            </Box>

            {warningCount > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                Review all flagged orders carefully before creating. Dose warnings are informational and do not block submission.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={creating}>Cancel</Button>

        {mode === 'input' && (
          <Button
            variant="contained"
            startIcon={generateMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <AIIcon />}
            disabled={!transcript.trim() || generateMutation.isLoading}
            onClick={() => generateMutation.mutate()}
          >
            {generateMutation.isLoading ? 'Analyzing…' : 'Generate Orders'}
          </Button>
        )}

        {mode === 'review' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
            disabled={orders.length === 0 || creating}
            onClick={handleCreateAll}
          >
            {creating
              ? `Creating ${createdCount}/${orders.length}…`
              : `Create ${orders.length} Order${orders.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIOrderGenerator;
